import { useState, useEffect } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Phone, MapPin, Instagram, Facebook, Youtube, Menu, X, UserRound, MessageCircle, ChevronDown, Star } from "lucide-react";
import { Logo } from "./Logo";
import { useAuth, useSite, isStaff } from "../context/AppContext";
import { waLink } from "../lib/api";

const NAV = [
  { to: "/", label: "Ana Sayfa" },
  { to: "/kurumsal", label: "Kurumsal" },
  { to: "/egitimlerimiz", label: "Eğitimlerimiz" },
  { to: "/egitim-takvimi", label: "Eğitim Takvimi" },
  { to: "/deneme-sinavlari", label: "Deneme Sınavları" },
  { to: "/egitim-videolari", label: "Eğitim Videoları" },
  { to: "/ders-notlari", label: "Ders Notları" },
  {
    label: "Daha Fazla", children: [
      { to: "/egitmenlerimiz", label: "Eğitmenlerimiz" }, { to: "/sss", label: "Sıkça Sorulan Sorular" },
      { to: "/duyurular", label: "Duyurular ve Haberler" }, { to: "/rehber", label: "Mevzuat ve Rehber" }, { to: "/yasal", label: "KVKK ve Yasal Metinler" },
    ],
  },
  { to: "/iletisim", label: "İletişim" },
];

const tel = (p = "") => "tel:+9" + p.replace(/\D/g, "");

function TopBar() {
  const s = useSite();
  const { user } = useAuth();
  return (
    <div className="bg-zinc-950 text-zinc-300 text-xs">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-10 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0">
          <a href={tel(s.phone || "0505 223 00 09")} className="flex items-center gap-1.5 hover:text-white" data-testid="topbar-phone"><Phone size={13} />{s.phone || "0543 778 75 73"}</a>
          <a href={waLink()} target="_blank" rel="noreferrer" className="hidden sm:flex items-center gap-1.5 hover:text-white" data-testid="topbar-whatsapp"><MessageCircle size={13} />WhatsApp</a>
          <span className="hidden md:flex items-center gap-1.5 truncate"><MapPin size={13} />Kuşadası / Aydın</span>
        </div>
        <div className="flex items-center gap-3">
          {s.socials?.instagram && <a href={s.socials.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="hover:text-white" data-testid="topbar-instagram"><Instagram size={14} /></a>}
          {s.socials?.facebook && <a href={s.socials.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="hover:text-white"><Facebook size={14} /></a>}
          {s.socials?.youtube && <a href={s.socials.youtube} target="_blank" rel="noreferrer" aria-label="YouTube" className="hover:text-white"><Youtube size={14} /></a>}
          <Link to={user ? (isStaff(user) ? "/yonetim" : "/kursiyer-paneli") : "/kursiyer-girisi"} className="flex items-center gap-1.5 bg-[#dc2626] text-white px-3 h-10 font-semibold" data-testid="topbar-student-login">
            <UserRound size={13} />{user ? (isStaff(user) ? "Yönetim Paneli" : "Kursiyer Paneli") : "Kursiyer Girişi"}
          </Link>
        </div>
      </div>
    </div>
  );
}

function DesktopNav() {
  const cls = ({ isActive }) => `whitespace-nowrap px-2.5 py-2 text-[13px] font-semibold hover:text-[#dc2626] ${isActive ? "text-[#dc2626]" : "text-zinc-800"}`;
  return (
    <nav className="hidden xl:flex items-center" aria-label="Ana menü">
      {NAV.map((n) => n.children ? (
        <div key={n.label} className="relative group">
          <button className="whitespace-nowrap px-2.5 py-2 text-[13px] font-semibold text-zinc-800 flex items-center gap-1" data-testid="nav-more">{n.label}<ChevronDown size={14} /></button>
          <div className="absolute left-0 top-full hidden group-hover:block group-focus-within:block bg-white border border-zinc-200 shadow-lg min-w-[230px] py-2 z-50">
            {n.children.map((c) => <NavLink key={c.to} to={c.to} className="block px-4 py-2 text-sm hover:bg-zinc-50 hover:text-[#dc2626]" data-testid={`nav-${c.to.slice(1)}`}>{c.label}</NavLink>)}
          </div>
        </div>
      ) : <NavLink key={n.to} to={n.to} end={n.to === "/"} className={cls} data-testid={`nav-${n.to.slice(1) || "home"}`}>{n.label}</NavLink>)}
    </nav>
  );
}

function MobileNav({ open, close }) {
  if (!open) return null;
  const items = NAV.flatMap((n) => n.children || [n]);
  return (
    <div className="xl:hidden border-t border-zinc-200 bg-white max-h-[75vh] overflow-y-auto" data-testid="mobile-menu">
      {items.map((n) => <NavLink key={n.to} to={n.to} end={n.to === "/"} onClick={close} className={({ isActive }) => `block px-5 py-3.5 text-sm font-semibold border-b border-zinc-100 ${isActive ? "text-[#dc2626]" : ""}`} data-testid={`mnav-${n.to.slice(1) || "home"}`}>{n.label}</NavLink>)}
      <div className="grid grid-cols-2 gap-2 p-4">
        <Link to="/kurs-basvurusu" onClick={close} className="btn-red" data-testid="mnav-apply">Hemen Başvur</Link>
        <Link to="/kursiyer-girisi" onClick={close} className="btn-dark" data-testid="mnav-login">Kursiyer Girişi</Link>
      </div>
    </div>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-zinc-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-[72px] flex items-center justify-between gap-4">
        <Link to="/" data-testid="header-logo-link"><Logo /></Link>
        <DesktopNav />
        <div className="flex items-center gap-2">
          <Link to="/kurs-basvurusu" className="btn-red hidden sm:inline-flex !py-2.5" data-testid="header-apply-btn">Hemen Başvur</Link>
          <button className="xl:hidden p-2.5 border border-zinc-300" onClick={() => setOpen(!open)} aria-label="Menüyü aç/kapat" data-testid="mobile-menu-toggle">{open ? <X size={20} /> : <Menu size={20} />}</button>
        </div>
      </div>
      <MobileNav open={open} close={() => setOpen(false)} />
    </header>
  );
}

function Footer() {
  const s = useSite();
  const col = (title, links) => (
    <div>
      <h3 className="text-white font-bold text-sm uppercase tracking-wider mb-4">{title}</h3>
      <ul className="space-y-2.5 text-sm">{links.map(([to, l]) => <li key={to}><Link to={to} className="hover:text-white">{l}</Link></li>)}</ul>
    </div>
  );
  return (
    <footer className="bg-zinc-950 text-zinc-400 border-t-4 border-[#dc2626]" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <Logo light />
          <p className="mt-4 text-sm leading-relaxed">{s.slogan || "PROFESYONEL EĞİTİM • GÜNCEL BİLGİ • UYGULAMALI EĞİTİM"}</p>
        </div>
        {col("Eğitim", [["/egitimlerimiz", "Eğitimlerimiz"], ["/egitim-takvimi", "Eğitim Takvimi"], ["/kurs-basvurusu", "Kurs Başvurusu"], ["/deneme-sinavlari", "Deneme Sınavları"], ["/egitim-videolari", "Eğitim Videoları"]])}
        {col("Kurum", [["/kurumsal", "Kurumsal"], ["/egitmenlerimiz", "Eğitmenlerimiz"], ["/duyurular", "Duyurular"], ["/rehber", "Mevzuat ve Rehber"], ["/sss", "SSS"], ["/belge-dogrula", "Belge Doğrulama"], ["/yasal", "KVKK ve Yasal Metinler"]])}
        <div>
          <h3 className="text-white font-bold text-sm uppercase tracking-wider mb-4">İletişim</h3>
          <address className="not-italic text-sm space-y-2.5">
            <p>{s.address}</p>
            <p><a href={tel(s.phone || "")} className="hover:text-white" data-testid="footer-phone">{s.phone}</a></p>
            {s.email && <p><a href={`mailto:${s.email}`} className="hover:text-white">{s.email}</a></p>}
            {s.socials?.instagram && <p><a href={s.socials.instagram} target="_blank" rel="noreferrer" className="hover:text-white inline-flex items-center gap-1.5"><Instagram size={14} />@adaozelguvenlik</a></p>}
            {s.socials?.google && <p><a href={s.socials.google} target="_blank" rel="noreferrer" className="hover:text-white inline-flex items-center gap-1.5" data-testid="footer-google"><Star size={14} />Google'da Görüntüle / Yorum Yap</a></p>}
            {s.working_hours && <p data-testid="footer-hours">{s.working_hours}</p>}
          </address>
        </div>
      </div>
      <div className="border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 text-xs flex flex-col sm:flex-row justify-between gap-2">
          <span>© {new Date().getFullYear()} Ada Özel Güvenlik Eğitim Kurumu. Tüm hakları saklıdır.</span>
          <span className="flex gap-4"><Link to="/yasal/kvkk-aydinlatma-metni" className="hover:text-white">KVKK</Link><Link to="/yasal/cerez-politikasi" className="hover:text-white">Çerez Politikası</Link><Link to="/yasal/gizlilik-politikasi" className="hover:text-white">Gizlilik</Link></span>
        </div>
      </div>
    </footer>
  );
}

export const WhatsAppFab = () => (
  <a href={waLink()} target="_blank" rel="noreferrer" aria-label="WhatsApp ile yazın" data-testid="whatsapp-fab"
    className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-xl hover:scale-105" style={{ transition: "transform .2s" }}>
    <svg viewBox="0 0 32 32" width="28" height="28" fill="currentColor" aria-hidden="true"><path d="M16 3a13 13 0 0 0-11.2 19.6L3 29l6.6-1.7A13 13 0 1 0 16 3Zm0 23.7c-2 0-4-.6-5.7-1.6l-.4-.2-3.9 1 1-3.8-.3-.4A10.7 10.7 0 1 1 16 26.7Zm5.9-8c-.3-.2-1.9-1-2.2-1-.3-.1-.5-.2-.7.1l-1 1.3c-.2.2-.4.2-.7.1a8.8 8.8 0 0 1-4.4-3.8c-.3-.6.3-.5.9-1.7.1-.2 0-.4 0-.6l-1-2.4c-.3-.6-.5-.5-.7-.5h-.6c-.2 0-.6.1-.9.4-.3.3-1.2 1.1-1.2 2.8s1.2 3.2 1.4 3.5c.2.2 2.4 3.7 5.8 5.1 2.2.9 3 1 4.1.8.7-.1 1.9-.8 2.2-1.5.3-.8.3-1.4.2-1.5-.1-.2-.3-.3-.6-.4Z" /></svg>
  </a>
);

function CookieBanner() {
  const s = useSite();
  const [consent, setConsent] = useState(() => localStorage.getItem("ada_cookie_consent"));
  useEffect(() => {
    if (consent === "all" && s.ga_id && !window.gtag) {
      const sc = document.createElement("script");
      sc.async = true;
      sc.src = `https://www.googletagmanager.com/gtag/js?id=${s.ga_id}`;
      document.head.appendChild(sc);
      window.dataLayer = window.dataLayer || [];
      window.gtag = function () { window.dataLayer.push(arguments); };
      window.gtag("js", new Date());
      window.gtag("config", s.ga_id, { anonymize_ip: true });
    }
  }, [consent, s.ga_id]);
  if (consent) return null;
  const set = (v) => { localStorage.setItem("ada_cookie_consent", v); setConsent(v); };
  return (
    <div className="fixed bottom-0 inset-x-0 z-[60] bg-zinc-950 text-zinc-200 border-t-2 border-[#dc2626]" data-testid="cookie-banner">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col md:flex-row md:items-center gap-3 justify-between text-sm">
        <p>Sitemizde yalnızca zorunlu çerezler varsayılan olarak kullanılır. Analiz çerezleri yalnızca onayınızla etkinleşir. <Link to="/yasal/cerez-politikasi" className="underline">Çerez Politikası</Link></p>
        <div className="flex gap-2 shrink-0">
          <button className="btn-outline !py-2 text-white" onClick={() => set("necessary")} data-testid="cookie-necessary-btn">Sadece Zorunlu</button>
          <button className="btn-red !py-2" onClick={() => set("all")} data-testid="cookie-accept-btn">Tümünü Kabul Et</button>
        </div>
      </div>
    </div>
  );
}

export default function Layout() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return (
    <div className="min-h-screen flex flex-col">
      <a href="#icerik" className="sr-only focus:not-sr-only focus:absolute focus:z-[70] bg-white p-2">İçeriğe geç</a>
      <TopBar />
      <Header />
      <main id="icerik" className="flex-1"><Outlet /></main>
      <Footer />
      <WhatsAppFab />
      <CookieBanner />
    </div>
  );
}
